'use client';
import React from 'react';
import {
  DrawerRoot,
  DrawerTrigger,
  DrawerContainer,
  DrawerTitle,
  DrawerDismiss,
} from '@mentimeter/ragnar-ui/drawer';
import type { DisplayProperty } from '@mentimeter/ragnar-styled';
import { useTranslations } from 'next-intl';
import { Clickable as RagnarClickable } from '@mentimeter/ragnar-ui/clickable';
import { CrossIcon, MenuIcon } from '@mentimeter/ragnar-visuals';
import { ScreenReaderOnly } from '@mentimeter/ragnar-ui/screenreader-only';
import { Box } from '@mentimeter/ragnar-ui/box';
import { IconButton } from '@mentimeter/ragnar-ui/icon-button';
import { usePathname } from '@mentimeter/next-navigation';
import { isAuthenticated, type ContentUserT } from '../../state/user';
import type { MenuAction } from './types';
import ActionCollapsable from './ActionCollapsable';
import { LoginButton } from './LoginButton';
import { HeaderButton } from './HeaderButton';

interface MobileMenuProps {
  display: DisplayProperty[];
  actions: MenuAction[];
  displayOnMobile: DisplayProperty[];
  user: ContentUserT;
}

export const MobileMenu = ({
  display,
  actions,
  displayOnMobile,
  user,
}: MobileMenuProps) => {
  const t = useTranslations('first_impression.legacy.common');
  const pathname = usePathname();
  const [showMenu, setShowMenu] = React.useState(false);
  React.useEffect(() => {
    document.body.style.overflow = showMenu ? 'hidden' : 'auto';
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [showMenu]);

  React.useEffect(() => {
    setShowMenu(false);
  }, [pathname]);

  return (
    <DrawerRoot open={showMenu} onOpenChange={setShowMenu}>
      <DrawerTrigger>
        <RagnarClickable
          ml="space4"
          display={displayOnMobile}
          aria-label={t('basic.menu')}
          data-testid="page-header-menu"
          onClick={() => setShowMenu(!showMenu)}
          id="header-menuButton"
        >
          <MenuIcon size={5} color="primary" />
        </RagnarClickable>
      </DrawerTrigger>
      <DrawerContainer side="right">
        <Box
          display={display}
          flex="1 1 auto"
          width="100%"
          height="100%"
          data-testid="page-header-menu-container"
          alignItems="stretch"
          overflow="scroll"
        >
          <Box alignItems="stretch">
            <Box alignItems="flex-end">
              <ScreenReaderOnly>
                <DrawerTitle>{t('menu_items')}</DrawerTitle>
              </ScreenReaderOnly>
              <DrawerDismiss>
                <IconButton
                  variant="secondary"
                  aria-label={t('close_menu')}
                  onClick={() => setShowMenu(false)}
                  borderRadius="full"
                  size="large"
                >
                  <CrossIcon aria-hidden />
                </IconButton>
              </DrawerDismiss>
            </Box>
            <Box mb="space8" px="space8">
              {actions.map((a) => (
                <ActionCollapsable
                  action={a}
                  overviewLabel={t('page_links.see_more')}
                  openDropdownLabel={`${t('page_links.dropdown')} - ${a.name}`}
                  key={a.name}
                  setShowMenu={setShowMenu}
                />
              ))}
            </Box>
          </Box>
          <Box width={1} alignItems="center" px="space8" pb="space8">
            {!isAuthenticated(user) && <LoginButton />}
            <HeaderButton mt="space2" size="large" width={1} user={user} />
          </Box>
        </Box>
      </DrawerContainer>
    </DrawerRoot>
  );
};
